import { AlertCircle, RotateCw, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button as AriaButton, GridListItem } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';
import { MediaKindIcon } from './MediaKindIcon';
import { UploadProgressBar } from './UploadProgress';
import type { PendingUpload } from './useMediaUploads';

/** A pending tile's key among a collection's tiles. */
export function uploadTileKey(id: string): string {
  return `upload:${id}`;
}

/** A file on its way into a media collection: its progress, or its failure with Retry. */
export function PendingTile({
  upload,
  preview,
  dropLine,
  onRetry,
  onDismiss,
  portrait = false
}: {
  upload: PendingUpload;
  /** What the finished tile shows of an image upload; the kind's icon without it. */
  preview?: ReactNode;
  dropLine: ReactNode;
  onRetry(): void;
  onDismiss(): void;
  /** 4:5 instead of square. */
  portrait?: boolean;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  const media = { name: upload.file.name, mimeType: upload.file.type };
  return (
    <GridListItem
      id={uploadTileKey(upload.id)}
      textValue={upload.file.name}
      className={cn(
        'group/pending relative min-h-0 min-w-0 outline-none',
        portrait ? 'aspect-[4/5]' : 'aspect-square'
      )}
      aria-busy={upload.status === 'uploading'}
      data-fixed-tile
      data-testid="pending-tile"
    >
      <div className="absolute inset-0 overflow-hidden rounded-lg bg-bg-subtle ring-1 ring-border-default group-data-focus-visible/pending:ring-2 group-data-focus-visible/pending:ring-bg-emphasis/70 group-data-selected/pending:ring-2 group-data-selected/pending:ring-brand-500">
        {preview ? (
          <div
            className={cn(
              'absolute inset-0',
              upload.status === 'uploading' ? 'opacity-60' : 'opacity-30'
            )}
          >
            {preview}
          </div>
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-fg-muted">
            <MediaKindIcon media={media} className="size-8" />
          </span>
        )}
        {upload.status === 'uploading' ? (
          <UploadProgressBar
            progress={upload.progress}
            className="absolute inset-x-2.5 bottom-2.5"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-bg-danger/80 p-2 text-center">
            <span className="flex items-center gap-1 text-xs font-medium text-fg-danger">
              <AlertCircle className="size-3.5" />
              {t('uploadFailed')}
            </span>
            <span className="flex gap-1">
              <AriaButton
                onPress={onRetry}
                className={buttonVariants({ variant: 'secondary', size: 'xs' })}
              >
                <RotateCw className="size-3" />
                {t('retry')}
              </AriaButton>
              <AriaButton
                onPress={onDismiss}
                aria-label={t('dismiss', { name: upload.file.name })}
                className={buttonVariants({
                  variant: 'ghost',
                  size: 'xs',
                  layout: 'icon'
                })}
              >
                <X className="size-3" />
              </AriaButton>
            </span>
          </div>
        )}
      </div>
      {dropLine}
    </GridListItem>
  );
}
